import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  CreditCard, 
  Crown, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  ExternalLink,
  Settings,
  Calendar,
  Users,
  Zap,
  Gamepad2
} from 'lucide-react';
import { serverSubscriptionsAPI } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import toast from 'react-hot-toast';

const ServerSubscription = ({ guildId, guildName }) => {
  const [showPlans, setShowPlans] = useState(false);
  const queryClient = useQueryClient();

  // Fetch current subscription
  const { data: subscriptionData, isLoading, error } = useQuery(
    ['serverSubscription', guildId],
    () => serverSubscriptionsAPI.getServerSubscription(guildId, guildName).then(res => res.data),
    {
      enabled: !!guildId,
      onError: (err) => {
        // Don't show error for 404 (no subscription exists yet)
        if (err.response?.status !== 404) {
          toast.error('Failed to load subscription');
        }
      },
      retry: (failureCount, error) => {
        // Don't retry on 404 errors
        if (error?.response?.status === 404) {
          return false;
        }
        return failureCount < 2;
      }
    }
  );

  const subscription = subscriptionData?.data?.subscription || null;

  // Fetch available plans only when showPlans is true
  const { data: plansData, error: plansError, isLoading: plansLoading } = useQuery(
    'serverPlans',
    () => {
      console.log('🚀 Fetching server plans...');
      return serverSubscriptionsAPI.getServerPlans().then(res => {
        console.log('✅ Plans fetched successfully:', res.data);
        return res.data;
      });
    },
    {
      enabled: showPlans, // Only fetch when showPlans is true
      onError: (err) => {
        console.error('❌ Plans fetch error:', err);
        toast.error('Failed to load subscription plans. Please check your authentication.');
      },
      onSuccess: (data) => {
        console.log('✅ Plans loaded successfully:', data);
      }
    }
  );

  const plans = plansData?.data?.plans || [];

  // Create checkout session mutation
  const createCheckoutMutation = useMutation(
    ({ planId }) => serverSubscriptionsAPI.createServerSubscription(guildId, planId),
    {
      onSuccess: (response) => {
        console.log('🔍 Full response:', response);
        console.log('🔍 response.data:', response.data);
        console.log('🔍 response.data.data:', response.data?.data);
        console.log('🔍 response.data.data.checkoutUrl:', response.data?.data?.checkoutUrl);
        
        // Check if this is an upgrade
        const isUpgrade = subscription && subscription.planType !== 'free' && subscription.planType !== response.data?.data?.planId;
        
        if (isUpgrade) {
          toast.success('Redirecting to upgrade checkout...');
        } else {
          toast.success('Redirecting to checkout...');
        }
        
        // Extract checkoutUrl from the correct path
        const checkoutUrl = response.data?.data?.checkoutUrl;
        
        if (checkoutUrl) {
          console.log('🚀 Redirecting to:', checkoutUrl);
          window.location.href = checkoutUrl;
        } else {
          console.error('❌ No checkout URL found in response:', response);
          console.error('❌ Available keys in response.data:', Object.keys(response.data || {}));
          console.error('❌ Available keys in response.data.data:', Object.keys(response.data?.data || {}));
          toast.error('Failed to get checkout URL');
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to create checkout session');
      }
    }
  );

  // Create portal session mutation
  const createPortalMutation = useMutation(
    () => serverSubscriptionsAPI.createPortalSession(),
    {
      onSuccess: (data) => {
        // Redirect to Stripe customer portal
        window.location.href = data.url;
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to create portal session');
      }
    }
  );

  // Cancel subscription mutation
  const cancelSubscriptionMutation = useMutation(
    () => serverSubscriptionsAPI.cancelServerSubscription(guildId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['serverSubscription', guildId]);
        toast.success('Subscription will be canceled at the end of the current period');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to cancel subscription');
      }
    }
  );

  // Reactivate subscription mutation
  const reactivateSubscriptionMutation = useMutation(
    () => serverSubscriptionsAPI.reactivateServerSubscription(guildId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['serverSubscription', guildId]);
        toast.success('Subscription reactivated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to reactivate subscription');
      }
    }
  );

  const handleSubscribe = (planId) => {
    // Check if this is an upgrade
    const isUpgrade = subscription && subscription.planType !== 'free' && subscription.planType !== planId;
    
    if (isUpgrade) {
      if (window.confirm(`Are you sure you want to upgrade from ${subscription.planType} to ${planId}? This will cancel your current subscription and start a new one.`)) {
        createCheckoutMutation.mutate({ planId });
      }
    } else {
      createCheckoutMutation.mutate({ planId });
    }
  };

  const handleManageSubscription = () => {
    createPortalMutation.mutate();
  };

  const handleCancelSubscription = () => {
    if (window.confirm('Are you sure you want to cancel this subscription? It will remain active until the end of the current billing period.')) {
      cancelSubscriptionMutation.mutate();
    }
  };

  const handleReactivateSubscription = () => {
    reactivateSubscriptionMutation.mutate();
  };

  if (isLoading) return <LoadingSpinner />;

  // No subscription found (404 error or no data)
  if (!subscription || (error && error.response?.status === 404)) {
    return (
      <div className="space-y-6">
        {/* Current Free Plan Info */}
      <div className="bg-discord-darker p-6 rounded-lg border border-discord-dark">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full bg-discord-dark flex items-center justify-center">
                <span className="text-2xl">🆓</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-discord-lightest">Free Plan</h3>
                <p className="text-discord-light text-sm">{guildName}</p>
              </div>
            </div>
            
            <div className="px-3 py-1 rounded-full text-sm font-medium bg-discord-dark text-discord-light">
              Active
            </div>
          </div>

          {/* Free Plan Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="flex items-center space-x-3">
              <Users className="h-5 w-5 text-discord-blurple" />
              <div>
                <p className="text-discord-light text-sm">Max Users</p>
                <p className="text-discord-lightest font-medium">100</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Zap className="h-5 w-5 text-discord-blurple" />
              <div>
                <p className="text-discord-light text-sm">Games/Day</p>
                <p className="text-discord-lightest font-medium">10</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Gamepad2 className="h-5 w-5 text-discord-blurple" />
              <div>
                <p className="text-discord-light text-sm">Available Games</p>
                <p className="text-discord-lightest font-medium">2</p>
              </div>
            </div>
          </div>

          {/* Free Plan Games */}
          <div className="mb-6">
            <h4 className="text-discord-lightest font-medium mb-3">Available Games</h4>
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1 bg-discord-blurple/20 text-discord-blurple rounded-full text-sm font-medium">
                Tic Tac Toe
              </span>
              <span className="px-3 py-1 bg-discord-blurple/20 text-discord-blurple rounded-full text-sm font-medium">
                Number Guess
              </span>
            </div>
          </div>
        </div>

        {/* Upgrade Section */}
        <div className="bg-gradient-to-r from-discord-blurple/10 to-purple-500/10 p-6 rounded-lg border border-discord-blurple/20">
        <div className="text-center">
          <Crown className="h-16 w-16 text-discord-blurple mx-auto mb-4" />
          <h3 className="text-xl font-bold text-discord-lightest mb-2">
              Unlock Premium Features
          </h3>
          <p className="text-discord-light mb-6">
              Upgrade {guildName} to unlock more games, analytics, custom commands, and priority support.
          </p>
          
          {!showPlans ? (
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => {
                    console.log('🎯 View Premium Plans button clicked');
                    setShowPlans(true);
                  }}
                  className="btn btn-primary flex items-center space-x-2"
                >
                  <Crown className="h-4 w-4" />
                  <span>View Premium Plans</span>
                </button>
            <button
                  onClick={() => {
                    console.log('🎯 Compare Features button clicked');
                    setShowPlans(true);
                  }}
                  className="btn btn-secondary"
                >
                  Compare Features
            </button>
              </div>
            ) : (
              <div>
                {plansLoading && (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-discord-blurple mx-auto"></div>
                    <p className="text-discord-light mt-2">Loading plans...</p>
                  </div>
                )}
                
                {plansError && (
                  <div className="bg-red-900/20 border border-red-500 p-4 rounded-lg mb-4">
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="h-5 w-5 text-red-400" />
                      <span className="text-red-400 font-medium">Error Loading Plans</span>
                    </div>
                    <p className="text-red-300 text-sm mt-1">
                      Failed to load subscription plans. Please refresh the page and try again.
                    </p>
                    <button
                      onClick={() => window.location.reload()}
                      className="mt-2 px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
                    >
                      Refresh Page
                    </button>
                  </div>
                )}
                
                {!plansLoading && !plansError && (
                  <SubscriptionPlans 
                    plans={plans}
                    subscription={subscription}
                    onSubscribe={handleSubscribe}
                    isLoading={createCheckoutMutation.isLoading}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Active subscription
  return (
    <div className="bg-discord-darker p-8 rounded-2xl border border-discord-dark">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
            subscription?.planType === 'pro' 
              ? 'bg-gradient-to-r from-purple-500 to-pink-500' 
              : 'bg-discord-blurple'
          }`}>
            <Crown className="h-8 w-8 text-white" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-discord-lightest mb-1">
              {subscription?.planType ? subscription.planType.charAt(0).toUpperCase() + subscription.planType.slice(1) : 'Free'} Plan
            </h3>
            <p className="text-discord-light">{guildName}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <div className={`px-4 py-2 rounded-full text-sm font-semibold ${
            subscription?.status === 'active' ? 'bg-green-900 text-green-400 border border-green-700' :
            subscription?.status === 'trialing' ? 'bg-blue-900 text-blue-400 border border-blue-700' :
            subscription?.status === 'past_due' ? 'bg-yellow-900 text-yellow-400 border border-yellow-700' :
            'bg-red-900 text-red-400 border border-red-700'
          }`}>
            {subscription?.status === 'active' ? '✅ Active' :
             subscription?.status === 'trialing' ? '🆓 Trial' :
             subscription?.status === 'past_due' ? '⚠️ Past Due' :
             '❌ Inactive'}
          </div>
          {subscription?.planType === 'pro' && (
            <div className="px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-purple-500 to-pink-500 text-white">
              ⭐ Premium
            </div>
          )}
        </div>
      </div>

      {/* Subscription Details */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-discord-darkest p-6 rounded-xl border border-discord-dark hover:border-discord-blurple/30 transition-colors">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <Calendar className="h-6 w-6 text-blue-400" />
            </div>
            <div>
              <p className="text-discord-light text-sm font-medium mb-1">Next Billing</p>
              <p className="text-discord-lightest font-bold text-xl">
                {subscription?.currentPeriodEnd ? 
                  new Date(subscription.currentPeriodEnd).toLocaleDateString() : 
                  'N/A'
                }
              </p>
            </div>
          </div>
        </div>

        <div className="bg-discord-darkest p-6 rounded-xl border border-discord-dark hover:border-discord-blurple/30 transition-colors">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
              <Users className="h-6 w-6 text-green-400" />
            </div>
            <div>
              <p className="text-discord-light text-sm font-medium mb-1">Max Users</p>
              <p className="text-discord-lightest font-bold text-xl">
                {subscription.limits?.serverMembers === 'unlimited' ? '∞' : subscription.limits?.serverMembers || 100}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-discord-darkest p-6 rounded-xl border border-discord-dark hover:border-discord-blurple/30 transition-colors">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <Zap className="h-6 w-6 text-purple-400" />
            </div>
            <div>
              <p className="text-discord-light text-sm font-medium mb-1">Games Today</p>
              <p className="text-discord-lightest font-bold text-xl">
                {subscription.gamesToday || 0} / {subscription.limits?.gamesPerDay === 'unlimited' ? '∞' : subscription.limits?.gamesPerDay || 10}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="mb-8">
        <h4 className="text-xl font-bold text-discord-lightest mb-4">Plan Features</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-2">
            {subscription.features?.customCommands ? (
              <CheckCircle className="h-4 w-4 text-green-400" />
            ) : (
              <XCircle className="h-4 w-4 text-red-400" />
            )}
            <span className="text-discord-light text-sm">Custom Commands</span>
          </div>
          <div className="flex items-center space-x-2">
            {subscription.features?.analytics ? (
              <CheckCircle className="h-4 w-4 text-green-400" />
            ) : (
              <XCircle className="h-4 w-4 text-red-400" />
            )}
            <span className="text-discord-light text-sm">Analytics</span>
          </div>
          <div className="flex items-center space-x-2">
            {subscription.features?.prioritySupport ? (
              <CheckCircle className="h-4 w-4 text-green-400" />
            ) : (
              <XCircle className="h-4 w-4 text-red-400" />
            )}
            <span className="text-discord-light text-sm">Priority Support</span>
          </div>
          <div className="flex items-center space-x-2">
            {subscription.features?.customThemes ? (
              <CheckCircle className="h-4 w-4 text-green-400" />
            ) : (
              <XCircle className="h-4 w-4 text-red-400" />
            )}
            <span className="text-discord-light text-sm">Custom Themes</span>
          </div>
        </div>
      </div>

      {/* Available Games */}
      <div className="mb-8">
        <h4 className="text-xl font-bold text-discord-lightest mb-4">Available Games</h4>
        <div className="flex flex-wrap gap-2">
          {subscription.availableGames?.map((game) => (
            <span 
              key={game}
              className="px-3 py-1 bg-discord-blurple/20 text-discord-blurple rounded-full text-sm font-medium"
            >
              {game.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </span>
          ))}
        </div>
      </div>

      {/* Trial Notice */}
      {subscription.isTrialActive && (
        <div className="bg-blue-900/20 border border-blue-500 p-4 rounded-lg mb-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-blue-400" />
            <span className="text-blue-400 font-medium">Trial Active</span>
          </div>
          <p className="text-blue-300 text-sm mt-1">
            Your trial ends on {new Date(subscription.trialEnd).toLocaleDateString()}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={handleManageSubscription}
          disabled={createPortalMutation.isLoading}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Settings className="h-4 w-4" />
          <span>Manage Billing</span>
          <ExternalLink className="h-4 w-4" />
        </button>

        {subscription.status === 'active' && (
          <button
            onClick={handleCancelSubscription}
            disabled={cancelSubscriptionMutation.isLoading}
            className="btn btn-secondary"
          >
            Cancel Subscription
          </button>
        )}

        {subscription.status === 'canceled' && (
          <button
            onClick={handleReactivateSubscription}
            disabled={reactivateSubscriptionMutation.isLoading}
            className="btn btn-primary"
          >
            Reactivate Subscription
          </button>
        )}

        {/* Downgrade to Free option for Pro users */}
        {subscription.planType === 'pro' && subscription.status === 'active' && (
          <button
            onClick={() => {
              if (window.confirm('Are you sure you want to downgrade to the Free plan? You will lose access to premium features immediately.')) {
                handleCancelSubscription();
              }
            }}
            disabled={cancelSubscriptionMutation.isLoading}
            className="px-4 py-2 border border-orange-400 text-orange-400 rounded-lg hover:bg-orange-400 hover:text-white transition-colors"
          >
            Downgrade to Free
          </button>
        )}
      </div>
    </div>
  );
};

const SubscriptionPlans = ({ plans, subscription, onSubscribe, isLoading }) => {
  if (!plans || plans.length === 0) {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="h-12 w-12 text-discord-light mx-auto mb-4" />
        <p className="text-discord-light">No subscription plans available.</p>
      </div>
    );
  }

  // Filter out free plan from premium plans
  const premiumPlans = plans.filter(plan => plan.id !== 'free');
  const freePlan = plans.find(plan => plan.id === 'free');

  return (
    <div className="space-y-8 mt-6">
      {/* Hero Section */}
      <div className="text-center">
        <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-discord-blurple/20 to-purple-500/20 px-4 py-2 rounded-full mb-4">
          <Crown className="h-5 w-5 text-discord-blurple" />
          <span className="text-discord-blurple font-medium">Choose Your Plan</span>
        </div>
        <h3 className="text-2xl font-bold text-discord-lightest mb-2">
          Unlock Premium Features
        </h3>
        <p className="text-discord-light max-w-2xl mx-auto">
          Upgrade your Discord server with powerful features, unlimited games, and priority support. 
          Start with our free plan and upgrade anytime.
        </p>
      </div>

      {/* Plan Cards - Beautiful Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
        {/* Free Plan Card */}
        <div className="bg-discord-darker p-8 rounded-2xl border border-discord-dark hover:border-discord-light/30 transition-all duration-300">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-discord-dark rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">🆓</span>
            </div>
            <h4 className="text-xl font-bold text-discord-lightest mb-2">Free Plan</h4>
            <div className="text-4xl font-bold text-discord-light mb-2">$0</div>
            <p className="text-discord-light text-sm">Forever</p>
          </div>

          <div className="space-y-4 mb-8">
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
              <span className="text-discord-light">Up to 100 server members</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
              <span className="text-discord-light">10 games per day</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
              <span className="text-discord-light">Basic games (Tic Tac Toe, Number Guess)</span>
            </div>
            <div className="flex items-center space-x-3">
              <XCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
              <span className="text-discord-light/60">Analytics dashboard</span>
            </div>
            <div className="flex items-center space-x-3">
              <XCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
              <span className="text-discord-light/60">Custom commands</span>
            </div>
            <div className="flex items-center space-x-3">
              <XCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
              <span className="text-discord-light/60">Priority support</span>
            </div>
          </div>

          <button
            disabled
            className="w-full py-3 px-6 rounded-lg font-medium bg-discord-dark text-discord-light cursor-not-allowed"
          >
            Current Plan
          </button>
        </div>

        {/* Premium Plans */}
        {premiumPlans.map((plan, index) => (
          <div key={plan.id} className={`bg-discord-darkest p-8 rounded-2xl border-2 transition-all duration-300 hover:scale-105 relative ${
            plan.id === 'pro' 
              ? 'border-discord-blurple bg-gradient-to-b from-discord-darkest to-discord-blurple/5' 
              : 'border-discord-dark hover:border-discord-light/50'
          }`}>
            {plan.id === 'pro' && (
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                  ⭐ Most Popular
                </span>
              </div>
            )}
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-discord-blurple to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">
                  {plan.id === 'pro' ? '⭐' : plan.id === 'premium' ? '💎' : '🏢'}
                  </span>
              </div>
              <h4 className="text-xl font-bold text-discord-lightest mb-2">{plan.name} Plan</h4>
              <div className="text-4xl font-bold text-discord-blurple mb-2">
                ${(plan.price / 100).toFixed(2)}
              </div>
              <p className="text-discord-light text-sm">per {plan.interval}</p>
            </div>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                <span className="text-discord-light">
                  {plan.limits?.serverMembers === 'unlimited' ? '∞' : plan.limits?.serverMembers || 10000} server members
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                <span className="text-discord-light">
                  {plan.limits?.gamesPerDay === 'unlimited' ? '∞' : plan.limits?.gamesPerDay || 'Unlimited'} games per day
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                <span className="text-discord-light">
                  All available games ({plan.availableGames?.length || 'All'})
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                <span className="text-discord-light">Analytics dashboard</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                <span className="text-discord-light">Custom commands</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                <span className="text-discord-light">Priority support</span>
              </div>
          </div>

            <button
              onClick={() => onSubscribe(plan.id)}
              disabled={isLoading}
              className={`w-full py-4 px-6 rounded-lg font-semibold text-lg transition-all duration-300 ${
                plan.id === 'pro'
                  ? 'bg-gradient-to-r from-discord-blurple to-purple-500 hover:from-discord-blurple/90 hover:to-purple-500/90 text-white shadow-lg hover:shadow-xl'
                  : 'bg-discord-blurple hover:bg-discord-blurple/90 text-white'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Processing...</span>
                </div>
              ) : (
                <>
                  <Crown className="h-5 w-5 inline mr-2" />
                  {subscription && subscription.planType !== 'free' && subscription.planType !== plan.id 
                    ? `Switch to ${plan.name}` 
                    : `Upgrade to ${plan.name}`
                  }
                </>
              )}
            </button>
        </div>
      ))}
      </div>

      {/* Feature Comparison Table */}
      <div className="bg-discord-darkest rounded-2xl border border-discord-dark overflow-hidden mt-8">
        <div className="p-6 border-b border-discord-dark">
          <h4 className="text-xl font-bold text-discord-lightest">Detailed Feature Comparison</h4>
          <p className="text-discord-light mt-1">Compare all features across our plans</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-discord-dark bg-discord-darker">
                <th className="text-left p-6 text-discord-light font-semibold">Features</th>
                <th className="text-center p-6 text-discord-light font-semibold">
                  <div className="flex items-center justify-center space-x-2">
                    <span className="text-2xl">🆓</span>
                    <span>Free</span>
                  </div>
                </th>
                {premiumPlans.map((plan) => (
                  <th key={plan.id} className="text-center p-6 text-discord-light font-semibold">
                    <div className="flex items-center justify-center space-x-2">
                      <span className="text-2xl">
                        {plan.id === 'pro' ? '⭐' : plan.id === 'premium' ? '💎' : '🏢'}
                      </span>
                      <span>{plan.name}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-discord-dark/30">
                <td className="p-6 text-discord-lightest font-medium">Games per day</td>
                <td className="text-center p-6">
                  <span className="bg-discord-dark text-discord-light px-3 py-1 rounded-full text-sm">10</span>
                </td>
                {premiumPlans.map((plan) => (
                  <td key={plan.id} className="text-center p-6">
                    <span className="bg-green-900 text-green-400 px-3 py-1 rounded-full text-sm font-medium">
                      {plan.limits?.gamesPerDay === 'unlimited' ? '∞ Unlimited' : plan.limits?.gamesPerDay || 'Unlimited'}
                    </span>
                  </td>
                ))}
              </tr>
              <tr className="border-b border-discord-dark/30">
                <td className="p-6 text-discord-lightest font-medium">Server members</td>
                <td className="text-center p-6">
                  <span className="bg-discord-dark text-discord-light px-3 py-1 rounded-full text-sm">100</span>
                </td>
                {premiumPlans.map((plan) => (
                  <td key={plan.id} className="text-center p-6">
                    <span className="bg-green-900 text-green-400 px-3 py-1 rounded-full text-sm font-medium">
                      {plan.limits?.serverMembers === 'unlimited' ? '∞ Unlimited' : plan.limits?.serverMembers?.toLocaleString() || '10,000'}
                    </span>
                  </td>
                ))}
              </tr>
              <tr className="border-b border-discord-dark/30">
                <td className="p-6 text-discord-lightest font-medium">Available games</td>
                <td className="text-center p-6">
                  <span className="bg-discord-dark text-discord-light px-3 py-1 rounded-full text-sm">2 games</span>
                </td>
                {premiumPlans.map((plan) => (
                  <td key={plan.id} className="text-center p-6">
                    <span className="bg-green-900 text-green-400 px-3 py-1 rounded-full text-sm font-medium">
                      All Games
                    </span>
                  </td>
                ))}
              </tr>
              <tr className="border-b border-discord-dark/30">
                <td className="p-6 text-discord-lightest font-medium">Analytics dashboard</td>
                <td className="text-center p-6">
                  <XCircle className="h-6 w-6 text-red-400 mx-auto" />
                </td>
                {premiumPlans.map((plan) => (
                  <td key={plan.id} className="text-center p-6">
                    <CheckCircle className="h-6 w-6 text-green-400 mx-auto" />
                  </td>
                ))}
              </tr>
              <tr className="border-b border-discord-dark/30">
                <td className="p-6 text-discord-lightest font-medium">Custom commands</td>
                <td className="text-center p-6">
                  <XCircle className="h-6 w-6 text-red-400 mx-auto" />
                </td>
                {premiumPlans.map((plan) => (
                  <td key={plan.id} className="text-center p-6">
                    <CheckCircle className="h-6 w-6 text-green-400 mx-auto" />
                  </td>
                ))}
              </tr>
              <tr className="border-b border-discord-dark/30">
                <td className="p-6 text-discord-lightest font-medium">Priority support</td>
                <td className="text-center p-6">
                  <XCircle className="h-6 w-6 text-red-400 mx-auto" />
                </td>
                {premiumPlans.map((plan) => (
                  <td key={plan.id} className="text-center p-6">
                    <CheckCircle className="h-6 w-6 text-green-400 mx-auto" />
                  </td>
                ))}
              </tr>
              <tr>
                <td className="p-6 text-discord-lightest font-medium">Custom themes</td>
                <td className="text-center p-6">
                  <XCircle className="h-6 w-6 text-red-400 mx-auto" />
                </td>
                {premiumPlans.map((plan) => (
                  <td key={plan.id} className="text-center p-6">
                    <CheckCircle className="h-6 w-6 text-green-400 mx-auto" />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Trust & Security */}
      <div className="text-center py-6">
        <div className="flex items-center justify-center space-x-6 text-discord-light text-sm">
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-4 w-4 text-green-400" />
            <span>Secure payment with Stripe</span>
          </div>
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-4 w-4 text-green-400" />
            <span>Cancel anytime</span>
          </div>
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-4 w-4 text-green-400" />
            <span>30-day money back guarantee</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServerSubscription;
